import { useMutation } from "@apollo/client";
import {
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  InternalAdminEditUserAttributeCategoryMutation,
  InternalAdminEditUserAttributeCategoryMutationVariables,
  InternalAdminUserAttributeCategoryFragment,
} from "gql/graphql";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import colors from "app/colors";
import {
  CancelIcon,
  CheckIcon,
  CloseIcon,
  PencilOutlineIcon,
} from "modules/icons";
import { FieldValues, useForm } from "react-hook-form";
import { getMessageFromError } from "modules/utils/Functions";
import { isEmpty } from "lodash";
import useIsHovering from "modules/hooks/use-is-hovering";
import useGlobalEditing from "modules/hooks/use-global-editing";
import useQueryString from "modules/hooks/use-query-string";
import useInternalAdminUserAttributeCategoryList from "../hooks/use-internal-admin-user-attribute-category-list";
import INTERNAL_ADMIN_EDIT_USER_ATTRIBUTE_CATEGORY_MUTATION from "../_networking/internal-admin-edit-user-attribute-category";

interface IProps {
  userAttributeCategory: InternalAdminUserAttributeCategoryFragment;
}

const InternalAdminEditUserAttributeCategoryName = ({
  userAttributeCategory,
}: IProps) => {
  const { qsOrganizationId } = useQueryString();
  const { isHovering, onMouseOut, onMouseOver } = useIsHovering();

  const { mode, setMode } = useGlobalEditing(
    `InternalAdminEditUserAttributeCategoryName-${userAttributeCategory.id}`,
  );

  const { userAttributeCategories } = useInternalAdminUserAttributeCategoryList(
    {
      organizationId: qsOrganizationId,
      input: {
        limit: 1000000,
        offset: 0,
      },
    },
  );

  const [internalAdminEditUserAttributeCategoryAPI, { loading }] = useMutation<
    InternalAdminEditUserAttributeCategoryMutation,
    InternalAdminEditUserAttributeCategoryMutationVariables
  >(INTERNAL_ADMIN_EDIT_USER_ATTRIBUTE_CATEGORY_MUTATION);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      name: userAttributeCategory.name || "",
    },
  });

  const onClearClicked = useCallback(() => {
    setValue("name", "");
  }, [setValue]);

  const name = watch("name");

  const isDuplicateName = useMemo(
    () =>
      !isEmpty(name) &&
      userAttributeCategories.some(
        (_userAttributeCategory) =>
          _userAttributeCategory.name.toLowerCase() === name.toLowerCase() &&
          _userAttributeCategory.id !== userAttributeCategory.id,
      ),
    [name, userAttributeCategory.id, userAttributeCategories],
  );

  useEffect(() => {
    if (isDuplicateName) {
      setError("name", {
        message: "This name is already taken",
      });
    } else {
      clearErrors("name");
    }
  }, [clearErrors, isDuplicateName, setError]);

  const onSubmit = useCallback(
    async ({ name }: FieldValues) => {
      if (!qsOrganizationId) return;
      internalAdminEditUserAttributeCategoryAPI({
        variables: {
          organizationId: qsOrganizationId,
          id: userAttributeCategory.id,
          input: {
            name,
          },
        },
        onError: (error) => {
          setError("name", {
            message: error.message,
          });
        },
        onCompleted: () => {
          setMode("display")();
        },
      });
    },
    [
      internalAdminEditUserAttributeCategoryAPI,
      qsOrganizationId,
      setError,
      setMode,
      userAttributeCategory.id,
    ],
  );

  if (mode === "edit") {
    return (
      <form
        id="admin-edit-user-attribute-category-name-form"
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: "100%" }}
      >
        <FormControl isInvalid={!!errors?.name} w="full">
          <Stack w="full" spacing={0}>
            <HStack w="full" spacing={1}>
              <InputGroup fontWeight={400} color={colors.gray50} fontSize={14}>
                <Input
                  id="name"
                  size="lg"
                  fontSize={24}
                  fontWeight={700}
                  color={colors.blackSecondary}
                  borderRadius={4}
                  borderWidth={2}
                  pl={5}
                  borderColor={colors.blue}
                  {...register("name")}
                />
                <InputRightElement h="100%" w={7}>
                  <IconButton
                    aria-label="clear name"
                    icon={<CancelIcon boxSize={5} fill={colors.gray40} />}
                    variant="ghost"
                    size="xxs"
                    onClick={onClearClicked}
                  />
                </InputRightElement>
              </InputGroup>
              <IconButton
                aria-label="save"
                type="submit"
                icon={
                  loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <CheckIcon boxSize={4} fill={colors.white} />
                  )
                }
                bg={colors.blue}
                variant="hover"
                size="xs"
                isDisabled={loading || isDuplicateName}
              />
              <IconButton
                aria-label="cancel"
                icon={<CloseIcon boxSize={4} fill={colors.gray40} />}
                borderColor={colors.gray40}
                variant="outline"
                size="xs"
                onClick={setMode("display")}
                isDisabled={loading}
              />
            </HStack>
            <FormErrorMessage m={0}>
              {getMessageFromError(errors?.name?.message)}
            </FormErrorMessage>
          </Stack>
        </FormControl>
      </form>
    );
  }

  return (
    <HStack
      w="full"
      spacing={4}
      borderRadius={4}
      py="6px"
      px={1}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
      _hover={{
        backgroundColor: colors.blue20,
      }}
    >
      <Text
        fontSize={24}
        fontWeight={700}
        color={colors.blackSecondary}
        w="full"
      >
        {userAttributeCategory.name}
      </Text>
      <IconButton
        aria-label="Edit user attribute category name"
        icon={<PencilOutlineIcon boxSize={6} fill={colors.gray40} />}
        variant="ghost"
        size="xxs"
        onClick={setMode("edit")}
        visibility={isHovering ? "visible" : "hidden"}
      />
    </HStack>
  );
};

export default memo(InternalAdminEditUserAttributeCategoryName);
